import React, { useCallback, useEffect, useState } from "react";
import { Gauge, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { hrs } from "./Processing";

const Bar = ({ value, tone = "bg-primary" }) => (
  <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted">
    <span className={cn("block h-full rounded-full", tone)} style={{ width: `${Math.min(100, value)}%` }} />
  </span>
);

const Card = ({ title, hint, children, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-xs" data-testid={testid}>
    <p className="font-display text-sm font-semibold">{title}</p>
    {hint && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

export const ProcessingDashboard = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [thinking, setThinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/processing/analytics");
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const insights = async (refresh = false) => {
    setThinking(true);
    try {
      const { data } = await api.get("/processing/insights", { params: { refresh } });
      setAi(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setThinking(false); }
  };

  const applyBenchmarks = async () => {
    try {
      await api.post("/processing/benchmarks", { benchmarks: ai.suggested_benchmarks });
      toast.success("Benchmarks updated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const k = data.kpis;

  return (
    <div className="space-y-5" data-testid="processing-dashboard">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[["Jobs", k.jobs], ["Delivered", k.delivered], ["Pending", k.pending],
          ["Past due", k.delayed], ["Rework", `${k.rework_pct}%`], ["Avg efficiency", `${k.avg_efficiency}%`]]
          .map(([label, value]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className="num text-xl font-bold" data-testid={`pdash-${label.toLowerCase().replace(/ /g, "-")}`}>{value}</p>
            </div>
          ))}
      </div>

      <Card title="Engineer productivity" testid="pdash-engineers"
        hint="Efficiency compares the hours actually worked with the hours the job should have taken for its size and complexity.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="py-2 text-left">Engineer</th>
                <th className="py-2 text-right">Jobs</th>
                <th className="py-2 text-right">Delivered</th>
                <th className="py-2 text-right">Active</th>
                <th className="py-2 text-right">Expected</th>
                <th className="py-2 text-right">Size units</th>
                <th className="py-2 text-right">Rework</th>
                <th className="py-2 text-right">Late</th>
                <th className="py-2 text-right">Efficiency</th>
              </tr>
            </thead>
            <tbody>
              {data.engineers.map((e) => (
                <tr key={e.name} className="border-b border-border/60">
                  <td className="py-2 font-medium">{e.name}</td>
                  <td className="num py-2 text-right">{e.jobs}</td>
                  <td className="num py-2 text-right">{e.delivered}</td>
                  <td className="num py-2 text-right">{hrs(e.active_hours)}</td>
                  <td className="num py-2 text-right text-muted-foreground">{hrs(e.expected_hours)}</td>
                  <td className="num py-2 text-right">{e.units}</td>
                  <td className="num py-2 text-right">{e.rework_pct}%</td>
                  <td className="num py-2 text-right">{e.late}</td>
                  <td className="py-2 text-right">
                    <span className={cn("num text-sm font-bold",
                      e.efficiency >= 100 ? "text-success" : e.efficiency >= 75 ? "text-foreground" : "text-destructive")}>
                      {e.efficiency}%
                    </span>
                    <Bar value={e.efficiency} tone={e.efficiency >= 100 ? "bg-success" : e.efficiency >= 75 ? "bg-primary" : "bg-destructive"} />
                  </td>
                </tr>
              ))}
              {data.engineers.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-sm text-muted-foreground">No jobs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="By service type" testid="pdash-types" hint="Average hours actually spent per job.">
          <div className="space-y-2">
            {data.types.map((t) => (
              <div key={t.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5">
                <span className="text-sm">{t.name}</span>
                <span className="num text-xs text-muted-foreground">
                  {t.jobs} job(s) &middot; avg {hrs(t.avg_hours)} &middot; {t.efficiency}%
                </span>
              </div>
            ))}
            {data.types.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
          </div>
        </Card>

        <Card title="Time per project" testid="pdash-projects">
          <div className="space-y-2">
            {data.projects.map((p) => (
              <div key={p.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5">
                <span className="min-w-0 truncate text-sm">{p.name}
                  <span className="block text-xs text-muted-foreground">{p.customer_name}</span>
                </span>
                <span className="num text-sm">{hrs(p.active_hours)}</span>
              </div>
            ))}
            {data.projects.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
          </div>
        </Card>

        <Card title="Monthly trend" testid="pdash-trend" hint="Jobs, hours worked and efficiency by month.">
          <div className="space-y-2">
            {data.trend.map((m) => (
              <div key={m.name} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5">
                <span className="num text-sm">{m.name}</span>
                <span className="num text-xs text-muted-foreground">
                  {m.jobs} job(s) &middot; {hrs(m.active_hours)} &middot; {m.efficiency}%
                </span>
              </div>
            ))}
            {data.trend.length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
          </div>
        </Card>

        <Card title="Past due jobs" testid="pdash-delayed">
          <div className="space-y-2">
            {data.delayed_jobs.map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5">
                <span className="min-w-0 truncate text-sm">{j.project_name}
                  <span className="block text-xs text-muted-foreground">{j.engineer_name} &middot; {j.stage}</span>
                </span>
                <span className="text-xs font-semibold text-destructive">
                  {j.late_days}d late &middot; {fdate(j.due_date)}
                </span>
              </div>
            ))}
            {data.delayed_jobs.length === 0 && <p className="text-sm text-muted-foreground">Nothing overdue.</p>}
          </div>
        </Card>
      </div>

      <Card title="Learned benchmarks" testid="pdash-benchmarks"
        hint="Hours per size unit (1 sq km, or 500 images, or 10 GB - whichever is larger), taken from your own delivered jobs.">
        <div className="grid gap-2 sm:grid-cols-3">
          {Object.entries(data.benchmarks).map(([type, b]) => (
            <div key={type} className="rounded-lg border border-border px-3 py-2">
              <p className="text-sm font-medium">{type}</p>
              <p className="num text-xs text-muted-foreground">
                {b.hours_per_unit} h / unit &middot; {b.source}{b.samples ? ` (${b.samples} job(s))` : ""}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{data.formula}</p>
      </Card>

      <Card title="AI reading of the numbers" testid="pdash-ai">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="soft" disabled={thinking} data-testid="pdash-ai-run" onClick={() => insights(!!ai)}>
            {thinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {ai ? "Refresh the review" : "Review the productivity data"}
          </Button>
          {ai?.generated_at && (
            <span className="text-xs text-muted-foreground">last run {fdatetime(ai.generated_at)}</span>
          )}
        </div>
        {ai && (
          <div className="mt-3 space-y-2 text-sm">
            {ai.headline && <p className="font-medium">{ai.headline}</p>}
            {!!(ai.observations || []).length && (
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                {ai.observations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            )}
            {!!(ai.watch_outs || []).length && (
              <div className="rounded-lg border border-warning/40 bg-warning-soft/40 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-warning">Watch out</p>
                <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                  {ai.watch_outs.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {!!(ai.recommendations || []).length && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Suggested actions</p>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  {ai.recommendations.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {!!(ai.suggested_kpis || []).length && (
              <p className="text-xs text-muted-foreground"><b>KPIs worth adding:</b> {ai.suggested_kpis.join(", ")}</p>
            )}
            {isAdmin && ai.suggested_benchmarks && Object.keys(ai.suggested_benchmarks).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary-soft/40 px-3 py-2">
                <Gauge className="h-4 w-4 text-primary" />
                <span className="text-xs">
                  Suggested benchmark change: {Object.entries(ai.suggested_benchmarks).map(([t, v]) => `${t} ${v} h/unit`).join(", ")}
                </span>
                <Button size="sm" variant="outline" data-testid="pdash-apply-benchmarks" onClick={applyBenchmarks}>
                  Apply
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
